/**
 * Seminar API Endpoints
 * Centralizes all seminar-related API calls.
 *
 * `id` is the canonical Strapi v5 identity (documentId) — use utils/identity.apiId
 * at call sites. Endpoints return the raw axios response; callers unwrap via
 * services/response.
 */
import type { AxiosResponse } from "axios";
import api from "../api";
import type { ApiListResponse, ApiResponse, EntityPayload, QueryParams } from "../../types/api";
import type { Seminar } from "../../types/entities";

export const seminarApi = {
  /** Get all seminars with optional filters/sort/populate. */
  getAll: (params: QueryParams = {}): Promise<ApiListResponse<Seminar>> =>
    api.get("/api/seminars", { params }),

  /** Get a single seminar by documentId. */
  getById: (id: string, params: QueryParams = {}): Promise<ApiResponse<Seminar>> =>
    api.get(`/api/seminars/${id}`, { params }),

  /** Get seminars by referent (custom route; filter data in the body). */
  getByReferent: (data: EntityPayload): Promise<AxiosResponse> =>
    api.post("/api/seminars/getbyreferent", { data }),

  create: (data: EntityPayload): Promise<ApiResponse<Seminar>> =>
    api.post("/api/seminars", { data }),

  update: (id: string, data: EntityPayload): Promise<ApiResponse<Seminar>> =>
    api.put(`/api/seminars/${id}`, { data }),

  delete: (id: string): Promise<AxiosResponse> => api.delete(`/api/seminars/${id}`),
};
